import classNames from 'classnames';
import { useMemo, useState } from 'react';

import { Enrolment } from '../../../../api/types/enrolment';
import { Student } from '../../../../api/types/student';
import Cross from '../../../../assets/images/icon-filled-cross-dark.svg';
import useSubscriptionContext from '../useSubscriptionContext';
import { Cancellation } from './Cancellation';
import { Confirmation } from './Confirmation';
import { SpecialOffer } from './SpecialOffer';
import { StudentDetails } from './components/StudentDetails';

export interface DetailsProps {
  student: Student | null;
}

export const Details = ({ student }: DetailsProps) => {
  const {
    clueyPlus: {
      isLoading: isLoadingClueyPlus,
      details: { show: detailsShow },
      cancellation: { show: cancellationShow },
      specialOffer: { show: showSpecialOfferMessage },
      toast: { message: toastMessage, show: toastShow, setShow: setToastShow },
      confirmation: { show: confirmationShow, confirmationType },
      showDetailsDialog,
    },
    enrolments: {
      active: { enrolments: activeEnrolments, isLoading: isLoadingActiveEnrolments, error: activeEnrolmentError },
    },
  } = useSubscriptionContext();

  const [selectedEnrolment, setSelectedEnrolment] = useState<Enrolment | null>(null);

  const enrolments = useMemo(() => {
    return activeEnrolments.filter((enrolment) => enrolment.studentSfid === student?.studentId);
  }, [activeEnrolments, student]);

  const isLoading = isLoadingActiveEnrolments || isLoadingClueyPlus;
  const error = activeEnrolmentError;

  return (
    <div className="flex flex-col gap-y-3">
      {/*
        Handles showing of toast messages on top of the subscription details.
        Check the toast show prop in the useSubscriptionContext file.
       */}
      {toastShow && !isLoading && (
        <div className="flex w-full items-center gap-3 rounded-3xl bg-green-6 p-4 text-white shadow-lg">
          {toastMessage}
          <button
            type="button"
            onClick={() => setToastShow(false)}
            className="flex-shrink-0 p-1 hover:opacity-80"
            aria-label="Dismiss"
          >
            <img src={Cross} alt="" className="h-4 w-4 invert" />
          </button>
        </div>
      )}
      {/*
        Handles showing of the special offer message on top of the subscription details.
        Check the showSpecialOfferMessage prop in the useSubscriptionContext file.
       */}
      {showSpecialOfferMessage && !isLoading && <SpecialOffer />}

      {/*
        Handles showing of the subscription details (StudentDetails component).
        Check the detailsShow prop in the useSubscriptionContext file.
       */}
      <div className="relative flex h-full w-full flex-col" key={`${student?.studentId}-details`}>
        <div
          className={classNames(
            'flex h-full w-full flex-col rounded-3xl bg-[#01220D] p-7 text-white',
            !detailsShow && 'hidden'
          )}
        >
          {error && <div className="flex flex-col gap-y-4 font-semibold text-red-4">{error}</div>}
          <StudentDetails student={student} enrolments={enrolments} setSelectedEnrolment={setSelectedEnrolment} />
        </div>

        {/*
          Handles showing of the cancellation dialog.
          Check the cancellationShow and cancellationType props in the useSubscriptionContext file.
         */}
        {cancellationShow && (
          <Cancellation
            enrolment={selectedEnrolment}
            onClose={() => {
              showDetailsDialog();
              setSelectedEnrolment(null);
            }}
          />
        )}

        {/*
          Handles showing of the confirmation dialog.
          Check the confirmationShow and confirmationType props in the useSubscriptionContext file.
         */}
        {confirmationShow && (
          <Confirmation
            confirmationType={confirmationType}
            onClose={() => {
              showDetailsDialog();
            }}
          />
        )}
      </div>
    </div>
  );
};
